import React, { useRef, useEffect } from "react";
import Form from "react-bootstrap/Form";

function InputField({
  field,
  label,
  value,
  onChange,
  error,
  type = "text",
  as = "Form.Group",
  pattern = null,
  controlId = "applicant",
  options = [], // Add this for select options
  disabled = false,
  min = "",
  required = true,
  allowCommas = false,
  showDollarSign = false,
  formatPhone = false,
  placeholder
}) {
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  const formatPhoneNumber = (rawValue) => {
    const numericOnly = rawValue.replace(/\D/g, "").substring(0, 10);

    if (numericOnly.length > 6) {
      return `${numericOnly.substring(0, 3)}-${numericOnly.substring(3, 6)}-${numericOnly.substring(6)}`;
    }

    if (numericOnly.length > 3) {
      return `${numericOnly.substring(0, 3)}-${numericOnly.substring(3)}`;
    }

    return numericOnly;
  };

  const handleInputChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const oldValue = input.value;
    let newValue = e.target.value;

    if (formatPhone) {
      // Store original cursor position relative to numeric digits only
      const textBeforeCursor = oldValue.substring(0, cursorPos);
      const numericBeforeCursor = textBeforeCursor.replace(/\D/g, "");
      const oldNumericPos = numericBeforeCursor.length;
      
      // Remove all non-numeric characters
      const numericOnly = newValue.replace(/\D/g, "");
      
      // Limit to 10 digits (US phone number)
      const limitedNumeric = numericOnly.substring(0, 10);
      
      newValue = formatPhoneNumber(limitedNumeric);
      
      // Calculate new cursor position
      let newCursorPos = 0;
      let numericCount = 0;
      
      // Find position in formatted string where we have the same number of digits before cursor
      for (let i = 0; i < newValue.length; i++) {
        if (/\d/.test(newValue[i])) {
          numericCount++;
          if (numericCount === oldNumericPos) {
            // Position cursor after this digit
            newCursorPos = i + 1;
            break;
          }
        }
      }
      
      // If cursor was at the end or beyond, keep it at the end
      if (oldNumericPos >= limitedNumeric.length) {
        newCursorPos = newValue.length;
      }
      
      // Ensure cursor position is valid
      if (newCursorPos < 0) {
        newCursorPos = 0;
      }
      
      cursorPositionRef.current = newCursorPos;
    } else if (showDollarSign) {
      // Store original cursor position relative to numeric digits only
      const textBeforeCursor = oldValue.substring(0, cursorPos);
      const validCharsBeforeCursor = textBeforeCursor.replace(/\$/g, "").replace(/,/g, "").replace(/[^\d.]/g, "");
      const oldValidPos = validCharsBeforeCursor.length;
      
      // Remove dollar sign and commas for processing
      newValue = newValue.replace(/\$/g, "").replace(/,/g, "");
      // Keep only digits and decimal points
      let cleaned = newValue.replace(/[^\d.]/g, "");
      
      // Ensure only one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }

      // Format with commas
      const split = cleaned.split('.');
      let integerPart = split[0];
      const decimalPart = split.length > 1 ? '.' + split[1] : '';
      
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      newValue = integerPart + decimalPart;
      
      // Calculate new cursor position
      let newCursorPos = 0;
      let validCount = 0;
      const dollarOffset = 1; // $ at the beginning
      
      // Find position in formatted string where we have the same number of valid chars before cursor
      for (let i = 0; i < newValue.length; i++) {
        if (newValue[i] !== ',') {
          validCount++;
          if (validCount === oldValidPos) {
            // Position cursor after this char
            newCursorPos = i + 1 + dollarOffset;
            break;
          }
        }
      }
      
      // If cursor was at the end or beyond, keep it at the end
      if (oldValidPos >= cleaned.length) {
        newCursorPos = newValue.length + dollarOffset;
      }
      
      // Ensure cursor position is valid
      if (newCursorPos < dollarOffset) {
        newCursorPos = dollarOffset;
      }
      
      cursorPositionRef.current = newCursorPos;
    } else if (allowCommas) {
      const textBeforeCursor = oldValue.substring(0, cursorPos);
      const validCharsBeforeCursor = textBeforeCursor.replace(/,/g, "").replace(/[^\d.]/g, "");
      const oldValidPos = validCharsBeforeCursor.length;
      
      newValue = newValue.replace(/,/g, "");
      
      let cleaned = newValue.replace(/[^\d.]/g, "");
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      
      const split = cleaned.split('.');
      let integerPart = split[0];
      const decimalPart = split.length > 1 ? '.' + split[1] : '';
      
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      newValue = integerPart + decimalPart;
      
      // Calculate cursor position for comma-formatted numbers
      let newCursorPos = 0;
      let validCount = 0;
      
      for (let i = 0; i < newValue.length; i++) {
        if (newValue[i] !== ',') {
          validCount++;
          if (validCount === oldValidPos) {
            newCursorPos = i + 1;
            break;
          }
        }
      }
      
      if (oldValidPos >= cleaned.length) {
        newCursorPos = newValue.length;
      }
      
      cursorPositionRef.current = newCursorPos;
    } else if (pattern === "^[0-9]*$") {
      newValue = newValue.replace(/\D/g, "");
      cursorPositionRef.current = cursorPos;
    } else {
      cursorPositionRef.current = cursorPos;
    }

    onChange(field, newValue);
  };

  // Set cursor position after value updates
  useEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== null && (showDollarSign || allowCommas || formatPhone)) {
      const pos = cursorPositionRef.current;
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(pos, pos);
          cursorPositionRef.current = null;
        }
      }, 0);
    }
  }, [value, showDollarSign, allowCommas, formatPhone]);

  // Ensure incoming values respect desired phone format
  useEffect(() => {
    if (!formatPhone || !value) {
      return;
    }

    const formatted = formatPhoneNumber(value);
    if (formatted !== value) {
      onChange(field, formatted);
    }
  }, [value, formatPhone, onChange, field]);

  // Format display value with dollar sign if needed
  const displayValue = showDollarSign && value && value !== "" && value !== "0" ? `$${value}` : (showDollarSign && !value ? "" : value);

  return (
    <Form.Group as={as} controlId={`${controlId}${field}`}>
      <Form.Label>{label}{required ? <span className="text-danger">*</span> : null}</Form.Label>
      {type === "select" ? ( // Check if type is "select"
        <Form.Select
          value={value}
          onChange={(e) => onChange(field, e.target.value)} // Pass the selected value
          isInvalid={!!error}
        >
          <option value="">Select {label}</option>
          {options.map((option, index) => {
            const optionValue = typeof option === "object" ? option.value : option;
            const optionLabel = typeof option === "object" ? option.label : option;
            return (
              <option key={index} value={String(optionValue)}>
                {optionLabel}
              </option>
            );
          })}
        </Form.Select>
      ) : (
        <Form.Control
          ref={inputRef}
          type={type}
          value={displayValue}
          onChange={handleInputChange}
          isInvalid={!!error}
          pattern={type === "text" && !formatPhone ? pattern : undefined}
          disabled={disabled}
          min={min}
          placeholder={placeholder !== undefined ? placeholder : undefined}
        />
      )}
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
}

export default InputField;
